/**
 * A room's page: its poster, kind, time and link, what to do with it, its tabs (Audience, Last live and Sessions
 * for a weekly live, Page and offer, Follow-ups), and the rail beside them.
 */
import { useEffect, useState } from "react";
import { Navigate, useLocation, useNavigate, useParams } from "react-router";
import { useAuth } from "../../auth/AuthContext";
import { ChangeTime, moveLiveTime, timeSavedLine } from "../../board/ChangeTime";
import { boardPath } from "../../board/paths";
import { InviteSheet, invitable, inviteTargetOf } from "../../components/InviteSheet";
import { useLayout } from "../../components/season/layout";
import { Button, IconButton } from "../../components/ui/Button";
import { EmptyState } from "../../components/ui/EmptyState";
import { Icon } from "../../components/ui/Icon";
import { Pill } from "../../components/ui/Pill";
import { useToast } from "../../components/ui/Toast";
import "../../styles/rooms.css";
import { AudienceTab } from "./AudienceTab";
import { FollowupsTab } from "./FollowupsTab";
import { LastLiveTab } from "./LastLiveTab";
import { shortDay } from "./liveWords";
import { PageTab } from "./PageTab";
import { RecordingStrip } from "./RecordingStrip";
import { ReplaySheet } from "./ReplaySheet";
import { CHANGE_TIME_STATE, copyRoomLink, movableLive, RoomMenu } from "./RoomActions";
import { RoomRail } from "./RoomRail";
import { callPath, KIND_TONE, kindOf, lengthLabel, replayReady, type RoomTab, tabPath, tabsFor, whenLine } from "./roomWords";
import { SeasonRoom } from "./SeasonRoom";
import { SessionsTab } from "./SessionsTab";
import { useRoomPage, useRoomTalk } from "./useRoomPage";

/** A room's page: the computer's on a computer, the Season page on a phone and an iPad. */
export function RoomView() {
  const layout = useLayout();
  return layout === "computer" ? <ComputerRoom /> : <SeasonRoom layout={layout} />;
}

function ComputerRoom() {
  const { id = "", tab: rawTab } = useParams<{ id: string; tab?: string }>();
  const { user } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const toast = useToast();
  const { detail, phase, series, last, lastLive, across, askAcrossAgain, onSaved, reload } = useRoomPage(id);
  const talk = useRoomTalk(id);
  const [inviting, setInviting] = useState(false);
  const [changing, setChanging] = useState(false);
  const [playing, setPlaying] = useState(false);
  const hostFirst = user?.first_name ?? "";

  // The card menu's Change the time lands here with the popover open.
  useEffect(() => {
    if ((location.state as typeof CHANGE_TIME_STATE | null)?.changeTime) {
      setChanging(true);
      navigate(location.pathname, { replace: true, state: null });
    }
  }, [location, navigate]);

  if (phase === "error" && !detail) {
    return (
      <div className="page rooms">
          <button type="button" className="back" onClick={() => navigate("/rooms")}>
          <Icon name="left" /> Rooms
        </button>
        <EmptyState icon="info" title="We couldn't find that room" actions={<Button onClick={() => navigate("/rooms")}>Back to Rooms</Button>} />
      </div>
    );
  }
  if (!detail) {
    return (
      <div className="page rooms" aria-busy="true">
          <button type="button" className="back" onClick={() => navigate("/rooms")}>
          <Icon name="left" /> Rooms
        </button>
        <div className="whead sk-head" />
      </div>
    );
  }
  // A draft is made on its board; a quick live has a page of its own.
  if (detail.status === "draft" && detail.board) return <Navigate to={boardPath(detail.id)} replace />;
  if (detail.quick_live) return <Navigate to={callPath(detail.id)} replace />;

  const tabs = tabsFor(detail);
  const tab = (tabs.find((t) => t.key === rawTab)?.key ?? "audience") as RoomTab;
  if (rawTab && rawTab !== tab) return <Navigate to={tabPath(detail.id, tab)} replace />;
  const live = detail.live;
  const kind = kindOf({ ...detail, live });
  // The page has room for the rhythm in full: "Every Tuesday · 6:00 PM ET".
  const when = live?.series ? live.series.long : whenLine({ ...detail, schedule_label: detail.schedule?.label ?? "", live });
  const length = lengthLabel({ duration_sec: detail.duration_sec, live });
  const published = detail.status === "published";
  const room = { ...detail, registered: detail.registered_here };
  const reportLive = series ? lastLive : null;
  const moving = movableLive(room) ? (live ?? null) : null;
  const openChange = () => (series ? navigate(tabPath(detail.id, "sessions")) : setChanging(true));

  const changeTime =
    changing && moving ? (
      <ChangeTime
        live={moving}
        seats={moving.registered}
        align="start"
        onSave={async (at, repeats) => {
          const moved = await moveLiveTime(detail.id, at, repeats);
          if (typeof moved === "string") return moved;
          toast(timeSavedLine(moved));
          reload();
          return "";
        }}
        onClose={() => setChanging(false)}
      />
    ) : null;

  return (
    <div className="page rooms">
      <button type="button" className="back" onClick={() => navigate("/rooms")}>
        <Icon name="left" /> Rooms
      </button>
      <div className="whead">
        <div className="thumb">
          {detail.poster_url ? <img src={detail.poster_url} alt="" /> : <span className="poster-scene" data-kind={kind} />}
        </div>
        <div className="wht" style={{ minWidth: 0 }}>
          <div className="row g8 wrap">
            <Pill tone={KIND_TONE[kind]} dot={kind !== "Draft"}>
              {kind}
            </Pill>
            {replayReady({ live: live && { ...live, replayPlays: live.replay.playable } }) ? <Pill>Replay ready</Pill> : null}
            <span className="small muted">{[when, length].filter(Boolean).join(" · ")}</span>
          </div>
          <h1 className="mt6" dir="auto">{detail.title}</h1>
          <div className="linkchip mt8">
            <Icon name="globe" />
            <span>{detail.register_url.replace(/^https?:\/\//, "")}</span>
            <IconButton icon="copy" label="Copy link" small onClick={() => void copyRoomLink(detail.register_url, toast)} />
          </div>
        </div>
        <div className="row g8 wha">
          <Button icon="link" onClick={() => void copyRoomLink(detail.register_url, toast)}>
            Copy link
          </Button>
          {invitable(room) ? (
            <Button icon="userplus" onClick={() => setInviting(true)}>
              Invite people
            </Button>
          ) : null}
          {published ? (
            <Button icon="eye" onClick={() => window.open(detail.register_url, "_blank", "noopener")}>
              Preview
            </Button>
          ) : null}
          <RoomMenu
            room={room}
            bordered
            onChanged={reload}
            onDeleted={() => navigate("/rooms", { replace: true })}
            onChangeTime={openChange}
            onTab={(t) => navigate(tabPath(detail.id, t))}
          />
        </div>
      </div>

      {!live ? (
        <RecordingStrip
          detailId={detail.id}
          videoId={detail.video_id}
          state={detail.video_state}
          durationSec={detail.duration_sec}
          onReady={reload}
        />
      ) : null}

      <nav className="utabs mt20" aria-label="The room">
        {tabs.map((t) => (
          <a
            key={t.key}
            href={tabPath(detail.id, t.key)}
            className={t.key === tab ? "utab on" : "utab"}
            aria-current={t.key === tab ? "page" : undefined}
            onClick={(e) => {
              e.preventDefault();
              navigate(tabPath(detail.id, t.key));
            }}
          >
            {t.label}
            {t.key === "live" && (last ?? live) ? <span className="n">{shortDay((last ?? live)!.startsAt, (last ?? live)!.timezone)}</span> : null}
          </a>
        ))}
      </nav>

      <div className="wlayout">
        <div style={{ minWidth: 0 }}>
          {tab === "audience" ? <AudienceTab key={location.key} detail={detail} hostFirst={hostFirst} circles={talk?.circles} /> : null}
          {tab === "live" ? (
            reportLive ? (
              <LastLiveTab webinarId={detail.id} title={detail.title} live={reportLive} seriesSession />
            ) : (
              <div className="card pad small muted">Getting the last live…</div>
            )
          ) : null}
          {tab === "sessions" && live ? <SessionsTab detail={detail} live={live} across={across} onSent={askAcrossAgain} hostFirst={hostFirst} onSaved={onSaved} /> : null}
          {tab === "page" ? <PageTab detail={detail} onChanged={reload} /> : null}
          {tab === "followups" ? <FollowupsTab detail={detail} /> : null}
        </div>
        <RoomRail detail={detail} talk={talk} onChangeTime={openChange} changeTime={changeTime} onWatchReplay={() => setPlaying(true)} />
      </div>

      {inviting ? (
        <InviteSheet
          webinar={inviteTargetOf(detail)}
          onClose={() => setInviting(false)}
          onSent={(send) => toast(`Sending ${send.total.toLocaleString("en-US")} ${send.total === 1 ? "invite" : "invites"}`)}
          onToast={toast}
        />
      ) : null}
      {playing ? (
        <ReplaySheet
          webinarId={detail.id}
          {...(last ? { sessionId: last.id } : live ? { sessionId: live.sessionId } : {})}
          title={detail.title}
          onClose={() => setPlaying(false)}
        />
      ) : null}
    </div>
  );
}
